import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  MapPinIcon, SparklesIcon, ArrowRightIcon, UserCircleIcon, 
  CheckCircleIcon, BuildingOffice2Icon
} from '@heroicons/react/24/outline';
import { MEMBERSHIP_HOPES, PEOPLE_CONTEXT, COMMON_COUNTRIES, COMPANY_SIZE } from '../../data/onboardingOptions';
import { updateUserWithFullData } from '../../utils/mailchimp';
import { MAILCHIMP_TAGS } from '../../utils/mailchimp-tags';

const TOTAL_STEPS = 4;

const OnboardingFlow = () => {
  const { user, completeOnboarding, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const mounted = useRef(true);

  // Form state
  const [step, setStep] = useState(1);
  const [country, setCountry] = useState('');
  const [membershipHope, setMembershipHope] = useState('');
  const [peopleContext, setPeopleContext] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [formData, setFormData] = useState({
    location: '',
    membership_hopes: '',
    people_context: '',
    company_size: ''
  });
  
  // UI states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Helper functions for labels - memoized to prevent recreation on each render
  const getHopeLabel = useCallback((id) => 
    MEMBERSHIP_HOPES.find(h => h.id === id)?.label || '', []);
    
  const getContextLabel = useCallback((id) => 
    PEOPLE_CONTEXT.find(c => c.id === id)?.label || '', []);
    
  const getCompanySizeLabel = useCallback((id) => 
    COMPANY_SIZE.find(c => c.id === id)?.label || '', []);
  
  // Load user data on mount
  useEffect(() => {
    if (!user) return;
    try {
      if (user.location) setCountry(user.location);
      if (user.motivations?.[0]) {
        const hopeId = MEMBERSHIP_HOPES.find(h => h.label === user.motivations[0])?.id;
        if (hopeId) setMembershipHope(hopeId);
      }
      if (user.context?.[0]) {
        const contextId = PEOPLE_CONTEXT.find(c => c.label === user.context[0])?.id;
        if (contextId) setPeopleContext(contextId);
      }
    } catch (err) {
      setError('Failed to load your profile data. Please try refreshing the page.');
    } finally {
      if (mounted.current) {
        setIsLoading(false);
      }
    }
  }, [user]);
  
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/signin');
    }
  }, [user, authLoading, navigate]);

  const nextStep = useCallback(() => {
    if (mounted.current) {
      setStep(current => Math.min(current + 1, TOTAL_STEPS));
    }
  }, []);
  
  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.location) {
          setError('Please enter your location');
          return false;
        }
        break;
      case 2:
        if (!formData.company_size) {
          setError('Please select your company size');
          return false;
        }
        break;
      case 3:
        if (!formData.membership_hopes) {
          setError('Please tell us about your membership hopes');
          return false;
        }
        break;
      case 4:
        if (!formData.people_context) {
          setError('Please tell us about the people in your context');
          return false;
        }
        break;
      default:
        return true;
    }
    return true;
  };

  const handleNext = () => {
    setError('');
    if (validateStep()) {
      setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setError('');
    setStep(prev => prev - 1);
  };

  // Final form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const updateData = {
        location: formData.location,
        headcount: getCompanySizeLabel(formData.company_size),
        motivations: [getHopeLabel(formData.membership_hopes)],
        context: [getContextLabel(formData.people_context)]
      };
      await completeOnboarding(updateData);

      // Update Mailchimp with goal/motivations after onboarding completion (non-blocking)
      const [firstName, ...lastNameParts] = (user?.name || '').trim().split(' ');
      const lastName = lastNameParts.join(' ');
      
      // Fire-and-forget with proper error handling
      (async () => {
        try {
          await updateUserWithFullData({
            email: user?.email,
            firstName: firstName || '',
            lastName: lastName || '',
            goal: getHopeLabel(formData.membership_hopes), // Add the goal to Mailchimp
            supabaseId: user?.id,
            tags: MAILCHIMP_TAGS.ONBOARDING_TAGS
          });
          // console.log('Successfully updated Mailchimp with onboarding data');
        } catch (mailchimpError) {
          console.error('Failed to update Mailchimp with onboarding data:', mailchimpError);
          // Could also send to error tracking service here
        }
      })();

      navigate('/payment?return_path=/onboarding');
    } catch (err) {
      setError(err.message || 'Failed to complete onboarding');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (isLoading) {
    return <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-primary">Loading your profile...</div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 max-w-2xl mx-auto">
        {/* Step indicators */}
        <div className="flex justify-center gap-6 mb-8">
          {[...Array(TOTAL_STEPS)].map((_, idx) => (
            <div key={idx} className={`h-10 w-10 rounded-full flex items-center justify-center border-2
              ${step === idx + 1 ? 'bg-primary text-white border-primary' : 
                step > idx + 1 ? 'bg-primary/80 text-white border-primary' : 
                'bg-gray-900 text-primary border-gray-700'}`}>
              {idx === 0 && <MapPinIcon className="h-5 w-5" />}
              {idx === 1 && <BuildingOffice2Icon className="h-5 w-5" />}
              {idx === 2 && <SparklesIcon className="h-5 w-5" />}
              {idx === 3 && <CheckCircleIcon className="h-5 w-5" />}
            </div>
          ))}
        </div>
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-2">
            {user?.avatar_url ? 
              <img src={user.avatar_url} alt="avatar" className="w-14 h-14 rounded-full border-2 border-primary" /> : 
              <UserCircleIcon className="w-14 h-14 text-primary" />
            }
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {user?.name ? `Welcome, ${user.name.split(' ')[0]}!` : 'Welcome!'}
          </h1>
          <p className="text-gray-400">Help us tailor your experience.</p>
        </div>
        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded text-red-300">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="location"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-xl font-bold text-white mb-4 text-center">Where is your company registered?</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                  {COMMON_COUNTRIES.map(({ code, label, flag }) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => handleChange('location', label)}
                      className={`flex items-center gap-2 p-2 rounded-lg border
                        ${formData.location === label ? 'bg-primary text-white border-primary' : 
                        'bg-gray-800 border-gray-700 text-gray-300'}`}
                    >
                      <span>{flag}</span>
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
                <button
                  onClick={handleNext}
                  disabled={!formData.location}
                  className="w-full py-3 px-4 bg-primary text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  type="button"
                >
                  Continue <ArrowRightIcon className="h-5 w-5" />
                </button>
              </motion.div>
            )}
            {step === 2 && (
              <motion.div
                key="company-size"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-xl font-bold text-white mb-4 text-center">How many people work at your company?</h2>
                <div className="grid grid-cols-1 gap-3 mb-6">
                  {COMPANY_SIZE.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => handleChange('company_size', id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border text-left
                        ${formData.company_size === id ? 'bg-primary/10 border-primary text-primary' : 
                        'bg-gray-800 border-gray-700 text-gray-300'}`}
                    >
                      <Icon className="h-6 w-6 flex-shrink-0" />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
                <button
                  onClick={handleNext}
                  disabled={!formData.company_size}
                  className="w-full py-3 px-4 bg-primary text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  type="button"
                >
                  Continue <ArrowRightIcon className="h-5 w-5" />
                </button>
              </motion.div>
            )}
            {step === 3 && (
              <motion.div
                key="hope"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-xl font-bold text-white mb-4 text-center">
                  What's your main goal for joining Open Org?
                </h2>
                <div className="grid grid-cols-1 gap-3 mb-6">
                  {MEMBERSHIP_HOPES.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => handleChange('membership_hopes', id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border text-left
                        ${formData.membership_hopes === id ? 'bg-primary/10 border-primary text-primary' : 
                        'bg-gray-800 border-gray-700 text-gray-300'}`}
                    >
                      <Icon className="h-6 w-6 flex-shrink-0" />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
                <button
                  onClick={handleNext}
                  disabled={!formData.membership_hopes}
                  className="w-full py-3 px-4 bg-primary text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  type="button"
                >
                  Continue <ArrowRightIcon className="h-5 w-5" />
                </button>
              </motion.div>
            )}
            {step === 4 && (
              <motion.div
                key="context"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-xl font-bold text-white mb-4 text-center">
                  What best describes your current People setup?
                </h2>
                <div className="grid grid-cols-1 gap-3 mb-6">
                  {PEOPLE_CONTEXT.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => handleChange('people_context', id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border text-left
                        ${formData.people_context === id ? 'bg-primary/10 border-primary text-primary' : 
                        'bg-gray-800 border-gray-700 text-gray-300'}`}
                    >
                      <Icon className="h-6 w-6 flex-shrink-0" />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
                <button
                  type="submit"
                  disabled={!formData.people_context || isSubmitting}
                  className="w-full py-3 px-4 bg-primary text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? 'Completing Setup...' : (<><span>Complete Setup</span> <SparklesIcon className="h-5 w-5" /></>)}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </div>
    </div>
  );
};

export default OnboardingFlow; 